import React, {useEffect, useState, useCallback, useMemo} from 'react';
import { Link } from 'react-router-dom';
import ZambiaMap from '../../components/ZambiaMap';
import {
    getGlobal,
    getAllSchemeApplications,
    getSchemeApplicationsByStatus,
    getAllHarvests,
    getCultivationArea,
    getFarmersByProvince,
    getFarmsByProvince,
    getAllInputDeliveries,
    getAllFarmInspections,
    getAllSchemes,
    getAllFarmerComplaints,
    getAllSupportEmails,
    getRecentActivity
} from "../../api/web";
import feather from "feather-icons";
import moment from "moment";

// District (nearest_town) -> province fallback when complaint has district but no province (e.g. Chongwe -> Lusaka)
const DISTRICT_TO_PROVINCE = { Chongwe: 'Lusaka', Kafue: 'Lusaka', Luangwa: 'Lusaka', Lusaka: 'Lusaka' };

const Dashboard = () => {
    const [dashboardData, setDashboardData] = useState({
        farmers: { count: 0, active: 0, inactive: 0 },
        companies: { count: 0, activeSchemes: 0 },
        schemeApplications: { total: 0, pending: 0, approved: 0, rejected: 0, underReview: 0 },
        agents: { count: 0, avgFarmersPerAgent: 0 },
        users: { count: 0 },
        harvest: { totalTonnage: 0, totalValue: 0, topCrops: [] },
        cultivationArea: { totalHectares: 0, totalAcres: 0 },
        inputDeliveries: { total: 0, pending: 0, confirmed: 0, delivered: 0 },
        inspections: { total: 0, completed: 0, pending: 0 },
        financial: { totalOwed: 0, cropValue: 0, inputCosts: 0 },
        complaints: { total: 0, pending: 0 },
        supportEmails: { total: 0, pending: 0 }
    });
    const [provinces, setProvinces] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [complaintsList, setComplaintsList] = useState([]);
    const [queryMapProvince, setQueryMapProvince] = useState(null);
    const [queryMapComplaintType, setQueryMapComplaintType] = useState(null);
    const [queryMapSeverity, setQueryMapSeverity] = useState(null);
    const [farmersByProvince, setFarmersByProvince] = useState({});
    const [farmsByProvince, setFarmsByProvince] = useState({});
    const [farmerMapSelectedProvince, setFarmerMapSelectedProvince] = useState(null);
    const [loading, setLoading] = useState(true);

    // Zambian provinces for Query Distribution map (same order as Flutter QueryMapPage)
    const QUERY_MAP_PROVINCES = useMemo(() => [
        'Western', 'Central', 'Eastern', 'Luapula', 'Northern', 'North-Western',
        'Southern', 'Copperbelt', 'Lusaka', 'Muchinga'
    ], []);

    const complaintsByProvince = useMemo(() => {
        const getRaw = (c) => {
            const fromFarm = c.farm?.province?.name || c.farm?.nearest_town?.region?.name;
            const fromPerson = c.complaint_by?.province?.name;
            const direct = c.province?.name;
            if (fromFarm || fromPerson || direct) return fromFarm || fromPerson || direct || '';
            const districtName = (c.farm?.nearest_town?.name || c.complaint_by?.town?.name || '').trim();
            if (districtName) {
                const key = Object.keys(DISTRICT_TO_PROVINCE).find(k => k.toLowerCase() === districtName.toLowerCase());
                if (key) return DISTRICT_TO_PROVINCE[key];
            }
            return '';
        };
        const norm = (name) => {
            if (!name) return null;
            const n = String(name).trim().toLowerCase().replace(/\s+/g, ' ').replace(/\bprovince\b/g, '').trim();
            return QUERY_MAP_PROVINCES.find(p => p.toLowerCase().replace(/-/g, ' ') === n || p.toLowerCase() === n.replace(/\s/g, '-')) || null;
        };
        const map = {};
        QUERY_MAP_PROVINCES.forEach(p => { map[p] = 0; });
        complaintsList.forEach(c => {
            const key = norm(getRaw(c));
            if (key && map[key] !== undefined) map[key] += 1;
        });
        return map;
    }, [complaintsList, QUERY_MAP_PROVINCES]);

    const filteredComplaints = useMemo(() => {
        const getRaw = (c) => {
            const fromFarm = c.farm?.province?.name || c.farm?.nearest_town?.region?.name;
            const fromPerson = c.complaint_by?.province?.name;
            const direct = c.province?.name;
            if (fromFarm || fromPerson || direct) return fromFarm || fromPerson || direct || '';
            const districtName = (c.farm?.nearest_town?.name || c.complaint_by?.town?.name || '').trim();
            if (districtName) {
                const key = Object.keys(DISTRICT_TO_PROVINCE).find(k => k.toLowerCase() === districtName.toLowerCase());
                if (key) return DISTRICT_TO_PROVINCE[key];
            }
            return '';
        };
        const norm = (name) => {
            if (!name) return null;
            const n = String(name).trim().toLowerCase().replace(/\s+/g, ' ').replace(/\bprovince\b/g, '').trim();
            return QUERY_MAP_PROVINCES.find(p => p.toLowerCase().replace(/-/g, ' ') === n || p.toLowerCase() === n.replace(/\s/g, '-')) || (name ? String(name).trim() : null);
        };
        return complaintsList.filter(c => {
            const rawProv = getRaw(c);
            const provName = norm(rawProv) || rawProv.trim();
            const type = (c.complaint_type || '').toLowerCase();
            const sev = (c.severity || '').toLowerCase();
            if (queryMapProvince && provName !== queryMapProvince) return false;
            if (queryMapComplaintType && type !== queryMapComplaintType.toLowerCase()) return false;
            if (queryMapSeverity && sev !== queryMapSeverity.toLowerCase()) return false;
            return true;
        });
    }, [complaintsList, queryMapProvince, queryMapComplaintType, queryMapSeverity, QUERY_MAP_PROVINCES]);

    const loadDashboardData = useCallback(async () => {
        try {
            // Helper function to get count from API response
            const getCount = (data) => {
                if (!data || data.error) return 0;
                if (data.count !== undefined) return data.count;
                if (Array.isArray(data)) return data.length;
                if (data.results && Array.isArray(data.results)) return data.results.length;
                return 0;
            };

            // Get all data in parallel
            const [
                globalData,
                allApps,
                pendingApps,
                approvedApps,
                rejectedApps,
                allHarvests,
                cultivationArea,
                farmersByProvinceRes,
                farmsByProvinceRes,
                inputDeliveries,
                farmInspections,
                allSchemes,
                farmerComplaints,
                supportEmails,
                activityResponse
            ] = await Promise.all([
                getGlobal(),
                getAllSchemeApplications(),
                getSchemeApplicationsByStatus('pending'),
                getSchemeApplicationsByStatus('approved'),
                getSchemeApplicationsByStatus('rejected'),
                getAllHarvests(),
                getCultivationArea(),
                getFarmersByProvince(),
                getFarmsByProvince(),
                getAllInputDeliveries(),
                getAllFarmInspections(),
                getAllSchemes(),
                getAllFarmerComplaints(),
                getAllSupportEmails(),
                getRecentActivity()
            ]);

            if (globalData.error) {
                console.error('Error loading global data');
                setLoading(false);
                return;
            }

            // Calculate farmers breakdown (mock for now - active/inactive)
            const farmersCount = globalData.farmers.count || 0;
            const activeFarmers = Math.floor(farmersCount * 0.92);
            const inactiveFarmers = farmersCount - activeFarmers;

            // Calculate agents average
            const agentsCount = globalData.agents.count || 0;
            const avgFarmersPerAgent = agentsCount > 0 ? Math.round(farmersCount / agentsCount) : 0;

            // Process scheme applications
            const totalApps = getCount(allApps.payload);
            const pendingCount = getCount(pendingApps.payload);
            const approvedCount = getCount(approvedApps.payload);
            const rejectedCount = getCount(rejectedApps.payload);
            const underReviewCount = Math.floor(pendingCount * 0.3);

            // Process schemes
            const schemesCount = getCount(allSchemes.payload);
            const companiesCount = globalData.company.count || 0;
            const activeSchemes = schemesCount;

            // Process harvest data
            const harvests = allHarvests.payload?.results || allHarvests.payload || [];
            let totalTonnage = 0;
            const cropMap = {};
            harvests.forEach(harvest => {
                if (harvest.tonnage) {
                    totalTonnage += parseFloat(harvest.tonnage) || 0;
                }
                if (harvest.crop) {
                    const cropName = harvest.crop.name || 'Unknown';
                    cropMap[cropName] = (cropMap[cropName] || 0) + (parseFloat(harvest.tonnage) || 0);
                }
            });
            const topCrops = Object.entries(cropMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([name, tonnage]) => ({ name, tonnage: Math.round(tonnage) }));

            // Process cultivation area
            const areaData = cultivationArea.payload || {};
            const totalHectares = areaData.total_combined_area_hectares || areaData.area_hectare || 0;
            const totalAcres = areaData.area_acres || 0;

            // Process input deliveries
            const deliveries = inputDeliveries.payload?.results || inputDeliveries.payload || [];
            const deliveryTotal = getCount(inputDeliveries.payload);
            const pendingDeliveries = deliveries.filter(d => !d.is_agent_confirmed && !d.is_farmer_confirmed).length;
            const confirmedDeliveries = deliveries.filter(d => d.is_agent_confirmed && !d.is_farmer_confirmed).length;
            const deliveredDeliveries = deliveries.filter(d => d.is_farmer_confirmed).length;

            // Process farm inspections
            const inspections = farmInspections.payload?.results || farmInspections.payload || [];
            const inspectionsTotal = getCount(farmInspections.payload);
            const completedInspections = inspections.filter(i => i.status === 'completed' || i.completed).length;
            const pendingInspections = inspectionsTotal - completedInspections;

            // Process financial data (mock calculation - would need to aggregate from all farmers)
            const financialTotalOwed = 0; // Would need to call get_amount_owed_to_farmer for each farmer
            const financialCropValue = totalTonnage * 500; // Mock: estimate value
            const financialInputCosts = financialCropValue * 0.3; // Mock: 30% of crop value

            // Process complaints
            const complaints = farmerComplaints.payload?.results || farmerComplaints.payload || [];
            const complaintsTotal = getCount(farmerComplaints.payload);
            const pendingComplaints = complaints.filter(c => !c.resolved || c.status === 'pending').length;
            setComplaintsList(Array.isArray(complaints) ? complaints : []);

            // Farmers per province (for map)
            const farmerProvinceList = farmersByProvinceRes?.payload || [];
            const farmerProvinceMap = {};
            const normProv = (name) => {
                if (!name) return null;
                const n = String(name).trim().toLowerCase().replace(/\s+/g, ' ').replace(/\bprovince\b/g, '').trim();
                return QUERY_MAP_PROVINCES.find(p => p.toLowerCase().replace(/-/g, ' ') === n || p.toLowerCase() === n.replace(/\s/g, '-')) || null;
            };
            QUERY_MAP_PROVINCES.forEach(p => { farmerProvinceMap[p] = 0; });
            farmerProvinceList.forEach(({ province, farmer_count }) => {
                const key = normProv(province?.name);
                if (key && farmerProvinceMap[key] !== undefined) farmerProvinceMap[key] += (farmer_count || 0);
            });
            setFarmersByProvince(farmerProvinceMap);

            // Farms per province (for table)
            const farmProvinceList = farmsByProvinceRes?.payload || [];
            const farmProvinceMap = {};
            QUERY_MAP_PROVINCES.forEach(p => { farmProvinceMap[p] = 0; });
            farmProvinceList.forEach(({ province, farm_count }) => {
                const key = normProv(province?.name);
                if (key && farmProvinceMap[key] !== undefined) farmProvinceMap[key] += (farm_count || 0);
            });
            setFarmsByProvince(farmProvinceMap);

            // Process support emails
            const emails = supportEmails.payload?.results || supportEmails.payload || [];
            const emailsTotal = getCount(supportEmails.payload);
            const pendingEmails = emails.filter(e => !e.resolved || e.status === 'open').length;

            setDashboardData({
                farmers: {
                    count: farmersCount,
                    active: activeFarmers,
                    inactive: inactiveFarmers
                },
                companies: {
                    count: companiesCount,
                    activeSchemes: activeSchemes
                },
                schemeApplications: {
                    total: totalApps,
                    pending: pendingCount,
                    approved: approvedCount,
                    rejected: rejectedCount,
                    underReview: underReviewCount
                },
                agents: {
                    count: agentsCount,
                    avgFarmersPerAgent: avgFarmersPerAgent
                },
                users: {
                    count: globalData.users.count || 0
                },
                harvest: {
                    totalTonnage: Math.round(totalTonnage),
                    totalValue: financialCropValue,
                    topCrops: topCrops
                },
                cultivationArea: {
                    totalHectares: Math.round(totalHectares),
                    totalAcres: Math.round(totalAcres)
                },
                inputDeliveries: {
                    total: deliveryTotal,
                    pending: pendingDeliveries,
                    confirmed: confirmedDeliveries,
                    delivered: deliveredDeliveries
                },
                inspections: {
                    total: inspectionsTotal,
                    completed: completedInspections,
                    pending: pendingInspections
                },
                financial: {
                    totalOwed: financialTotalOwed,
                    cropValue: financialCropValue,
                    inputCosts: financialInputCosts
                },
                complaints: {
                    total: complaintsTotal,
                    pending: pendingComplaints
                },
                supportEmails: {
                    total: emailsTotal,
                    pending: pendingEmails
                }
            });

            // Map activity log to recent activity display format
            const eventToIcon = {
                'user.created': { icon: 'user-plus', iconColor: 'bg-success-light tx-success' },
                'user.updated': { icon: 'user-plus', iconColor: 'bg-success-light tx-success' },
                'scheme.created': { icon: 'book-open', iconColor: 'bg-warning-light tx-orange' },
                'application.created': { icon: 'file-text', iconColor: 'bg-primary-light tx-primary' },
                'application.status_changed': { icon: 'check-circle', iconColor: 'bg-info-light tx-info' },
                'ussd.registration': { icon: 'phone', iconColor: 'bg-success-light tx-success' },
                'contract.created': { icon: 'file-text', iconColor: 'bg-primary-light tx-primary' }
            };
            const defaultIcon = { icon: 'activity', iconColor: 'bg-secondary-light tx-secondary' };
            const activityList = activityResponse?.payload && Array.isArray(activityResponse.payload) ? activityResponse.payload : [];
            const mappedActivities = activityList.slice(0, 10).map(item => {
                const { icon, iconColor } = eventToIcon[item.event] || defaultIcon;
                return {
                    id: item.id,
                    icon,
                    iconColor,
                    title: item.message || '—',
                    time: item.created_at ? moment(item.created_at).fromNow() : '—'
                };
            });
            setRecentActivities(mappedActivities);

            // Load provinces with real data
            try {
                // Get cultivation area data for all provinces
                const provinceData = await getCultivationArea('all');
                if (provinceData.payload && Array.isArray(provinceData.payload) && provinceData.payload.length > 0) {
                    // Map province data to our format
                    const provincesList = provinceData.payload.map(p => ({
                        name: p.province?.name || 'Unknown',
                        farmers: 0, // Would need separate endpoint for farmers per province
                        cultivationArea: p.cultivated_area_total || 0
                    }));
                    setProvinces(provincesList);
                } else {
                    // Fallback to mock data if API doesn't return expected format
                    const mockProvinces = [
                        { name: 'Eastern', farmers: 1038, cultivationArea: 1250 },
                        { name: 'Lusaka', farmers: 786, cultivationArea: 980 },
                        { name: 'Southern', farmers: 634, cultivationArea: 750 },
                        { name: 'Western', farmers: 521, cultivationArea: 620 },
                        { name: 'Northern', farmers: 456, cultivationArea: 540 },
                        { name: 'Central', farmers: 389, cultivationArea: 460 },
                        { name: 'Copperbelt', farmers: 312, cultivationArea: 380 },
                        { name: 'Luapula', farmers: 278, cultivationArea: 330 },
                        { name: 'Muchinga', farmers: 234, cultivationArea: 280 },
                        { name: 'North-Western', farmers: 198, cultivationArea: 240 }
                    ];
                    setProvinces(mockProvinces);
                }
            } catch (provinceError) {
                console.error('Error loading province data:', provinceError);
                // Fallback to mock data
                const mockProvinces = [
                    { name: 'Eastern', farmers: 1038, cultivationArea: 1250 },
                    { name: 'Lusaka', farmers: 786, cultivationArea: 980 },
                    { name: 'Southern', farmers: 634, cultivationArea: 750 },
                    { name: 'Western', farmers: 521, cultivationArea: 620 },
                    { name: 'Northern', farmers: 456, cultivationArea: 540 },
                    { name: 'Central', farmers: 389, cultivationArea: 460 },
                    { name: 'Copperbelt', farmers: 312, cultivationArea: 380 },
                    { name: 'Luapula', farmers: 278, cultivationArea: 330 },
                    { name: 'Muchinga', farmers: 234, cultivationArea: 280 },
                    { name: 'North-Western', farmers: 198, cultivationArea: 240 }
                ];
                setProvinces(mockProvinces);
            }
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, [QUERY_MAP_PROVINCES]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    useEffect(() => {
        if (!loading) {
            feather.replace();
        }
    }, [loading]);

    // Generate attention alerts from real data
    const attentionAlerts = [];

    if (dashboardData.schemeApplications.pending > 0) {
        attentionAlerts.push({
            icon: 'clock',
            iconColor: 'tx-orange',
            title: `${dashboardData.schemeApplications.pending} pending applications`,
            action: 'View Details &gt;',
            link: '/scheme_application'
        });
    }

    if (dashboardData.complaints.pending > 0) {
        attentionAlerts.push({
            icon: 'alert-triangle',
            iconColor: 'tx-danger',
            title: `${dashboardData.complaints.pending} pending complaints`,
            action: '&gt;',
            link: '/scheme_application'
        });
    }

    if (dashboardData.supportEmails.pending > 0) {
        attentionAlerts.push({
            icon: 'mail',
            iconColor: 'tx-primary',
            title: `${dashboardData.supportEmails.pending} support tickets pending`,
            action: '&gt;',
            link: '/scheme_application'
        });
    }

    if (dashboardData.inputDeliveries.pending > 0) {
        attentionAlerts.push({
            icon: 'package',
            iconColor: 'tx-warning',
            title: `${dashboardData.inputDeliveries.pending} input deliveries pending`,
            action: '&gt;',
            link: '/scheme_application'
        });
    }

    // Add default alerts if no real data
    if (attentionAlerts.length === 0) {
        attentionAlerts.push(
            {
                icon: 'clock',
                iconColor: 'tx-orange',
                title: '5 pending applications over 30 days',
                action: 'View Details &gt;',
                link: '/scheme_application'
            },
            {
                icon: 'file-text',
                iconColor: 'tx-primary',
                title: '12 schemes missing reports',
                action: '&gt;',
                link: '/scheme'
            },
            {
                icon: 'alert-circle',
                iconColor: 'tx-danger',
                title: '3 contracts expiring soon',
                action: '&gt;',
                link: '/scheme'
            }
        );
    }

    const formatNumber = (num) => {
        if (num >= 1000) {
            return num.toLocaleString();
        }
        return num.toString();
    };

    const calculatePercentage = (part, total) => {
        if (total === 0) return 0;
        return Math.round((part / total) * 100);
    };

    if (loading) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="text-center pd-y-50">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-body">
            <div className="container pd-x-0 pd-lg-x-10 pd-xl-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><span>Menu</span></li>
                                <li className="breadcrumb-item active" aria-current="page">Dashboard</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-0 tx-spacing--1">Dashboard</h4>
                    </div>
                </div>

                {/* Enhanced Summary Cards - Row 1 */}
                <div className="row row-xs g-2">
                    {/* Total Farmers Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Total Farmers</h6>
                                <span className="badge badge-success tx-white tx-10">7%</span>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.farmers.count)}</h3>
                                <p className="tx-11 tx-color-03 mg-b-0">
                                    <span className="tx-medium tx-success d-inline-flex align-items-center">
                                        {/* +128 this month <ion-icon name="arrow-up-outline" className="mg-l-2"></ion-icon> */}
                                    </span>
                                </p>
                            </div>
                            <div className="mg-t-15">
                                <div className="d-flex justify-content-between tx-12 mg-b-5">
                                    <span className="tx-color-03">Active: {formatNumber(dashboardData.farmers.active)}</span>
                                    <span className="tx-color-03">Inactive: {formatNumber(dashboardData.farmers.inactive)}</span>
                                </div>
                                <div className="progress ht-4 mg-b-0">
                                    <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{width: `${calculatePercentage(dashboardData.farmers.active, dashboardData.farmers.count)}%`}}
                                    ></div>
                                    <div
                                        className="progress-bar bg-gray-300"
                                        role="progressbar"
                                        style={{width: `${calculatePercentage(dashboardData.farmers.inactive, dashboardData.farmers.count)}%`}}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Total Companies Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-primary tx-semibold mg-b-0">Total Companies</h6>
                                <i data-feather="hash" className="tx-primary"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.companies.count)}</h3>
                                <p className="tx-11 tx-color-03 mg-b-0">
                                    <span className="tx-medium tx-success d-inline-flex align-items-center">
                                        {/* +2 this quarter <ion-icon name="arrow-up-outline" className="mg-l-2"></ion-icon> */}
                                        {/* TODO: Add this quarter */}
                                    </span>
                                </p>
                            </div>
                            <div className="mg-t-15">
                                <Link to="/scheme" className="d-flex align-items-center link-03 tx-12">
                                    {dashboardData.companies.activeSchemes === 0
                                        ? 'No active schemes'
                                        : <>Active Schemes: {formatNumber(dashboardData.companies.activeSchemes)} <ion-icon name="arrow-forward-outline" className="mg-l-5"></ion-icon></>
                                    }
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Scheme Applications Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-orange tx-semibold mg-b-0">Scheme Applications</h6>
                                <i data-feather="refresh-cw" className="tx-orange"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.schemeApplications.total)}</h3>
                            </div>
                            <div className="mg-t-15">
                                <div className="d-flex justify-content-between tx-12 mg-b-5">
                                    <span className="tx-color-03">Pending: {formatNumber(dashboardData.schemeApplications.pending)}</span>
                                    <span className="tx-color-03">Rejected: {formatNumber(dashboardData.schemeApplications.rejected)}</span>
                                </div>
                                <div className="d-flex align-items-center tx-12">
                                    <div className="progress flex-fill ht-4 mg-r-10">
                                        <div
                                            className="progress-bar bg-warning"
                                            role="progressbar"
                                            style={{width: `${calculatePercentage(dashboardData.schemeApplications.pending, dashboardData.schemeApplications.total)}%`}}
                                        ></div>
                                    </div>
                                    <span className="tx-medium">90</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Total Agents Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Total Agents</h6>
                                <i data-feather="users" className="tx-success"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.agents.count)}</h3>
                            </div>
                            <div className="mg-t-15">
                                <p className="tx-12 tx-color-03 mg-b-5">Avg Farmers/Agent: {formatNumber(dashboardData.agents.avgFarmersPerAgent)}</p>
                                <div className="progress ht-4">
                                    <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{width: '75%'}}
                                    ></div>
                                    <div
                                        className="progress-bar bg-warning"
                                        role="progressbar"
                                        style={{width: '20%'}}
                                    ></div>
                                    <div
                                        className="progress-bar bg-danger"
                                        role="progressbar"
                                        style={{width: '5%'}}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Summary Cards - Row 2 */}
                <div className="row row-xs g-2 mg-t-10">
                    {/* Total Cultivation Area Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-indigo tx-semibold mg-b-0">Total Cultivation Area</h6>
                                <i data-feather="map" className="tx-indigo"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.cultivationArea.totalHectares)}</h3>
                                <p className="tx-11 tx-color-03 mg-b-0">hectares</p>
                            </div>
                            <div className="mg-t-15">
                                <p className="tx-12 tx-color-03 mg-b-0">
                                    {formatNumber(dashboardData.cultivationArea.totalAcres)} acres
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Harvest Summary Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-teal tx-semibold mg-b-0">Total Harvest</h6>
                                <i data-feather="package" className="tx-teal"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.harvest.totalTonnage)}</h3>
                                <p className="tx-11 tx-color-03 mg-b-0">tonnes</p>
                            </div>
                            <div className="mg-t-15">
                                {dashboardData.harvest.topCrops.length > 0 && (
                                    <div className="tx-12 tx-color-03">
                                        Top: {dashboardData.harvest.topCrops[0]?.name || 'N/A'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Input Delivery Status Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-purple tx-semibold mg-b-0">Input Deliveries</h6>
                                <i data-feather="truck" className="tx-purple"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.inputDeliveries.total)}</h3>
                            </div>
                            <div className="mg-t-15">
                                <div className="d-flex justify-content-between tx-12 mg-b-5">
                                    <span className="tx-color-03">Pending: {formatNumber(dashboardData.inputDeliveries.pending)}</span>
                                    <span className="tx-color-03">Delivered: {formatNumber(dashboardData.inputDeliveries.delivered)}</span>
                                </div>
                                <div className="progress ht-4">
                                    <div
                                        className="progress-bar bg-warning"
                                        role="progressbar"
                                        style={{width: `${calculatePercentage(dashboardData.inputDeliveries.pending, dashboardData.inputDeliveries.total)}%`}}
                                    ></div>
                                    <div
                                        className="progress-bar bg-info"
                                        role="progressbar"
                                        style={{width: `${calculatePercentage(dashboardData.inputDeliveries.confirmed, dashboardData.inputDeliveries.total)}%`}}
                                    ></div>
                                    <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{width: `${calculatePercentage(dashboardData.inputDeliveries.delivered, dashboardData.inputDeliveries.total)}%`}}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Financial Summary Card */}
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-pink tx-semibold mg-b-0">Crop Value</h6>
                                <i data-feather="dollar-sign" className="tx-pink"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">ZMW {formatNumber(Math.round(dashboardData.financial.cropValue))}</h3>
                            </div>
                            <div className="mg-t-15">
                                <p className="tx-12 tx-color-03 mg-b-0">
                                    Input Costs: ZMW {formatNumber(Math.round(dashboardData.financial.inputCosts))}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scheme Application Pipeline and Input Delivery Pipeline Row */}
                <div className="row g-2 mg-t-10">
                    {/* Scheme Application Pipeline */}
                    <div className="col-lg-6">
                        <div className="card">
                            <div className="card-header pd-t-20 pd-b-15 bd-b-0">
                                <h6 className="mg-b-0">Scheme Application Pipeline</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-primary-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-primary">{formatNumber(dashboardData.schemeApplications.total)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-primary">Applied</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-primary rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-primary rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-primary rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-warning-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-warning">{formatNumber(dashboardData.schemeApplications.underReview)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-warning">Under Review</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-warning rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-warning rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-warning rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-success-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-success">{formatNumber(dashboardData.schemeApplications.approved)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-success">Approved</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-success rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-success rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-success rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-danger-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-danger">{formatNumber(dashboardData.schemeApplications.rejected)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-danger">Rejected</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-danger rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-danger rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-danger rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center mg-t-20">
                                    <Link to="/scheme_application" className="btn btn-sm btn-primary btn-uppercase">
                                        View applications
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Input Delivery Pipeline */}
                    <div className="col-lg-6">
                        <div className="card">
                            <div className="card-header pd-t-20 pd-b-15 bd-b-0">
                                <h6 className="mg-b-0">Input Delivery Pipeline</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-warning-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-warning">{formatNumber(dashboardData.inputDeliveries.pending)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-warning">Pending</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-warning rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-warning rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-warning rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-info-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-info">{formatNumber(dashboardData.inputDeliveries.confirmed)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-info">Confirmed</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-info rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-info rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-info rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-success-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-success">{formatNumber(dashboardData.inputDeliveries.delivered)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-success">Delivered</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-success rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-success rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-success rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-primary-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-primary">{formatNumber(dashboardData.inputDeliveries.total)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-primary">Total</h6>
                                            <div className="d-flex justify-content-center">
                                                <span className="d-inline-block wd-4 ht-4 bg-primary rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-primary rounded-circle mg-r-2"></span>
                                                <span className="d-inline-block wd-4 ht-4 bg-primary rounded-circle"></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Activity and Farmers Per Province Row */}
                <div className="row g-2 mg-t-10">
                    {/* Recent Activity */}
                    <div className="col-lg-4">
                        <div className="card ht-100p">
                            <div className="card-header pd-b-0 pd-x-20 bd-b-0">
                                <div className="d-sm-flex align-items-center justify-content-between">
                                    <h6 className="mg-b-0">Recent Activity</h6>
                                    <Link to="/activity" className="link-03 tx-12">View All &gt;</Link>
                                </div>
                            </div>
                            <div className="card-body pd-20">
                                <ul className="activity tx-13">
                                    {recentActivities.length === 0 && !loading ? (
                                        <li className="activity-item">
                                            <div className="activity-body">
                                                <p className="mg-b-0 tx-color-03">No recent activity</p>
                                            </div>
                                        </li>
                                    ) : (
                                        recentActivities.map((activity) => (
                                            <li key={activity.id} className="activity-item">
                                                <div className={`activity-icon ${activity.iconColor}`}>
                                                    <i data-feather={activity.icon}></i>
                                                </div>
                                                <div className="activity-body">
                                                    <p className="mg-b-2">{activity.title}</p>
                                                    <small className="tx-color-03">{activity.time}</small>
                                                </div>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Farmers Per Province - map + chart (replaces old card and standalone map card) */}
                    <div className="col-lg-8">
                        <div className="card ht-100p">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h6 className="mg-b-0">Farmers Per Province</h6>
                                <div className="tx-13 d-flex align-items-center">
                                    <span className="mg-r-5">Country:</span>
                                    <span className="d-flex align-items-center link-03 lh-0 d-inline-flex align-items-center">
                                        Zambia <ion-icon name="chevron-down-outline" className="mg-l-2"></ion-icon>
                                    </span>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row g-3 align-items-start">
                                    <div className="col-md-6">
                                        <div className="table-responsive">
                                            <table className="table table-borderless table-dashboard table-dashboard-one">
                                                <thead>
                                                    <tr>
                                                        <th className="wd-50">Province</th>
                                                        <th className="wd-25 text-end">Farmers</th>
                                                        <th className="wd-25 text-end">Farms</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const maxFarmers = Math.max(...QUERY_MAP_PROVINCES.map(p => farmersByProvince[p] ?? 0), 1);
                                                        return QUERY_MAP_PROVINCES.map((provName) => {
                                                            const count = farmersByProvince[provName] ?? 0;
                                                            const farmCount = farmsByProvince[provName] ?? 0;
                                                            const percentage = (count / maxFarmers) * 100;
                                                            return (
                                                                <tr key={provName}>
                                                                    <td className="tx-medium">{provName}</td>
                                                                    <td className="text-end">
                                                                        <div className="d-flex align-items-center justify-content-end">
                                                                            <div className="progress flex-fill ht-4 mg-r-10" style={{maxWidth: '100px'}}>
                                                                                <div
                                                                                    className="progress-bar bg-success"
                                                                                    role="progressbar"
                                                                                    style={{width: `${percentage}%`}}
                                                                                ></div>
                                                                            </div>
                                                                            <span className="tx-medium">{formatNumber(count)}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-end tx-medium">
                                                                        {formatNumber(farmCount)}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="rounded" style={{ maxWidth: 360, minHeight: 220, backgroundColor: '#f8f9fa' }}>
                                            <ZambiaMap
                                                selectedProvince={farmerMapSelectedProvince}
                                                onSelect={(name) => setFarmerMapSelectedProvince((prev) => (prev === name ? null : name))}
                                                canonicalProvinceNames={QUERY_MAP_PROVINCES}
                                                provinceCounts={farmersByProvince}
                                                compact
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Query Distribution - map card (similar to Flutter QueryMapPage) */}
                <div className="row g-2 mg-t-10">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header pd-b-0 pd-x-20 bd-b-0">
                                <h6 className="mg-b-5">Query Distribution</h6>
                                <p className="tx-color-03 tx-12 mg-b-0">Tap a province to see queries raised in that region</p>
                            </div>
                            <div className="card-body pd-20">
                                <div className="row g-3 align-items-start">
                                    {/* Left column: map */}
                                    <div className="col-md-5 col-lg-4">
                                        <div className="rounded mg-b-15" style={{ maxWidth: 420, minHeight: 240, backgroundColor: '#f8f9fa' }}>
                                            <ZambiaMap
                                                selectedProvince={queryMapProvince}
                                                onSelect={(name) => setQueryMapProvince((prev) => (prev === name ? null : name))}
                                                canonicalProvinceNames={QUERY_MAP_PROVINCES}
                                                provinceCounts={complaintsByProvince}
                                                compact
                                            />
                                        </div>
                                    </div>
                                    {/* Right column: province buttons, filters + list */}
                                    <div className="col-md-7 col-lg-8">
                                        <div className="d-flex flex-wrap gap-2 mg-b-15">
                                            {QUERY_MAP_PROVINCES.map((provName) => {
                                                const count = complaintsByProvince[provName] ?? 0;
                                                const isSelected = queryMapProvince === provName;
                                                return (
                                                    <button
                                                        key={provName}
                                                        type="button"
                                                        className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => setQueryMapProvince(isSelected ? null : provName)}
                                                        style={{ minWidth: '72px' }}
                                                    >
                                                        <span className="d-block tx-medium">{provName}</span>
                                                        <span className="tx-10 tx-color-03">{formatNumber(count)}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="row g-2 mg-b-15">
                                            <div className="col-4">
                                                <label className="tx-11 tx-semibold tx-uppercase tx-color-03 mg-b-5">Province</label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={queryMapProvince || ''}
                                                    onChange={(e) => setQueryMapProvince(e.target.value || null)}
                                                >
                                                    <option value="">All</option>
                                                    {QUERY_MAP_PROVINCES.map((p) => (
                                                        <option key={p} value={p}>{p}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-4">
                                                <label className="tx-11 tx-semibold tx-uppercase tx-color-03 mg-b-5">Query Type</label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={queryMapComplaintType || ''}
                                                    onChange={(e) => setQueryMapComplaintType(e.target.value || null)}
                                                >
                                                    <option value="">All</option>
                                                    <option value="drought">Drought</option>
                                                    <option value="pests">Pests</option>
                                                    <option value="flooding">Flooding</option>
                                                    <option value="fire">Fire</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                            <div className="col-4">
                                                <label className="tx-11 tx-semibold tx-uppercase tx-color-03 mg-b-5">Severity</label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={queryMapSeverity || ''}
                                                    onChange={(e) => setQueryMapSeverity(e.target.value || null)}
                                                >
                                                    <option value="">All</option>
                                                    <option value="low">Low</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="high">High</option>
                                                    <option value="extreme">Extreme</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="tx-13 mg-b-10">Queries ({filteredComplaints.length})</h6>
                                            {filteredComplaints.length === 0 ? (
                                                <p className="tx-color-03 tx-12 mg-b-0">No queries match the selected filters.</p>
                                            ) : (
                                                <ul className="list-unstyled mg-b-0">
                                                    {filteredComplaints.slice(0, 8).map((c) => (
                                                        <li key={c.id} className="d-flex align-items-center justify-content-between py-2 border-bottom border-color-gray-200">
                                                            <div>
                                                                <span className="tx-medium">{c.complaint_type || '—'}</span>
                                                                <span className="tx-11 tx-color-03 mg-l-5">{c.farm?.nearest_town?.name || c.complaint_by?.town?.name || c.farm?.province?.name || '—'}</span>
                                                            </div>
                                                            <span className={`badge ${(c.severity || '').toLowerCase() === 'high' || (c.severity || '').toLowerCase() === 'extreme' ? 'bg-danger' : 'bg-secondary'}`}>
                                                                {c.severity || '—'}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {filteredComplaints.length > 8 && (
                                                <p className="tx-12 tx-color-03 mg-t-10 mg-b-0">
                                                    Showing 8 of {formatNumber(filteredComplaints.length)} — adjust filters to narrow results.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Metrics Row */}
                <div className="row g-2 mg-t-10">
                    {/* Farm Inspections Card */}
                    <div className="col-lg-4">
                        <div className="card">
                            <div className="card-header pd-t-20 pd-b-15 bd-b-0">
                                <h6 className="mg-b-0">Farm Inspections</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6">
                                        <div className="text-center">
                                            <h3 className="tx-normal tx-rubik mg-b-5 tx-primary">{formatNumber(dashboardData.inspections.total)}</h3>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-0 tx-color-03">Total</h6>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-center">
                                            <h3 className="tx-normal tx-rubik mg-b-5 tx-success">{formatNumber(dashboardData.inspections.completed)}</h3>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-0 tx-color-03">Completed</h6>
                                        </div>
                                    </div>
                                </div>
                                <div className="mg-t-15">
                                    <div className="d-flex justify-content-between tx-12 mg-b-5">
                                        <span className="tx-color-03">Pending: {formatNumber(dashboardData.inspections.pending)}</span>
                                        <span className="tx-color-03">
                                            {dashboardData.inspections.total > 0
                                                ? `${calculatePercentage(dashboardData.inspections.completed, dashboardData.inspections.total)}% Complete`
                                                : '0% Complete'
                                            }
                                        </span>
                                    </div>
                                    <div className="progress ht-6">
                                        <div
                                            className="progress-bar bg-success"
                                            role="progressbar"
                                            style={{width: `${calculatePercentage(dashboardData.inspections.completed, dashboardData.inspections.total)}%`}}
                                        ></div>
                                        <div
                                            className="progress-bar bg-warning"
                                            role="progressbar"
                                            style={{width: `${calculatePercentage(dashboardData.inspections.pending, dashboardData.inspections.total)}%`}}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Crops Card */}
                    <div className="col-lg-4">
                        <div className="card">
                            <div className="card-header pd-t-20 pd-b-15 bd-b-0">
                                <h6 className="mg-b-0">Top Crops by Harvest</h6>
                            </div>
                            <div className="card-body">
                                {dashboardData.harvest.topCrops.length > 0 ? (
                                    <ul className="list-unstyled mg-b-0">
                                        {dashboardData.harvest.topCrops.map((crop, index) => {
                                            const maxTonnage = Math.max(...dashboardData.harvest.topCrops.map(c => c.tonnage), 1);
                                            const percentage = (crop.tonnage / maxTonnage) * 100;
                                            return (
                                                <li key={index} className="d-flex align-items-center mg-b-15">
                                                    <div className="wd-40 ht-40 rounded-circle bg-indigo-light d-flex align-items-center justify-content-center mg-r-10">
                                                        <span className="tx-indigo tx-semibold">{index + 1}</span>
                                                    </div>
                                                    <div className="flex-fill">
                                                        <p className="tx-medium mg-b-2">{crop.name}</p>
                                                        <div className="d-flex align-items-center">
                                                            <div className="progress flex-fill ht-4 mg-r-10">
                                                                <div
                                                                    className="progress-bar bg-indigo"
                                                                    role="progressbar"
                                                                    style={{width: `${percentage}%`}}
                                                                ></div>
                                                            </div>
                                                            <span className="tx-12 tx-medium">{formatNumber(crop.tonnage)}t</span>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p className="tx-color-03 tx-12 mg-b-0 text-center">No harvest data available</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Support & Complaints Summary */}
                    <div className="col-lg-4">
                        <div className="card">
                            <div className="card-header pd-t-20 pd-b-15 bd-b-0">
                                <h6 className="mg-b-0">Support & Complaints</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6">
                                        <div className="text-center">
                                            <h3 className="tx-normal tx-rubik mg-b-5 tx-danger">{formatNumber(dashboardData.complaints.pending)}</h3>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-0 tx-color-03">Pending Complaints</h6>
                                            <p className="tx-12 tx-color-03 mg-b-0 mg-t-5">Total: {formatNumber(dashboardData.complaints.total)}</p>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-center">
                                            <h3 className="tx-normal tx-rubik mg-b-5 tx-primary">{formatNumber(dashboardData.supportEmails.pending)}</h3>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-0 tx-color-03">Support Tickets</h6>
                                            <p className="tx-12 tx-color-03 mg-b-0 mg-t-5">Total: {formatNumber(dashboardData.supportEmails.total)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Attention Needed Row */}
                <div className="row g-2 mg-t-10">
                    {/* Attention Needed */}
                    <div className="col-lg-4">
                        <div className="card ht-100p">
                            <div className="card-header pd-b-0 pd-x-20 bd-b-0">
                                <h6 className="mg-b-0">Attention Needed</h6>
                            </div>
                            <div className="card-body pd-20">
                                <ul className="list-unstyled mg-b-0">
                                    {attentionAlerts.map((alert, index) => (
                                        <li key={index} className="d-flex align-items-start mg-b-20">
                                            <div className={`wd-40 ht-40 rounded-circle d-flex align-items-center justify-content-center mg-r-10 ${alert.iconColor.includes('tx-') ? 'bg-' + alert.iconColor.replace('tx-', '') + '-light' : ''}`}>
                                                <i data-feather={alert.icon} className={alert.iconColor}></i>
                                            </div>
                                            <div className="flex-fill">
                                                <p className="tx-medium mg-b-5">{alert.title}</p>
                                                <a href={alert.link} className="link-03 tx-12">{alert.action.replace('>', '&gt;')}</a>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default Dashboard;
